import { useCallback, useEffect, useState } from 'react'
import { API_BASE } from './constants'
import { resolveSectionFromLocation } from './sectionRouting'
import { fetchWithPolicy } from '../../lib/api/fetchPolicy'
import { clearAuthToken, getAuthToken, setAuthToken } from '../../lib/api/authToken'
import { isProtectedAdminPath, isPublicAdminPath } from './adminRouteGuard'
import type { AdminLoginForm } from './useAdminAuth.types'
import { bootstrapAdminCsrfToken } from '../../lib/api/csrfToken'
import type { AdminLoginStage } from './useAdminAuth.shared'
import { resolveAccessTokenRefreshDelayMs } from './sessionRefresh'
export type { AdminLoginStage } from './useAdminAuth.shared'

const REFRESH_RETRY_DELAY_MS = 200
const SESSION_REFRESH_FAILURE_RETRY_MS = 10_000

let adminSessionBootstrapInFlight: Promise<string | null> | null = null

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const requestSessionBootstrapToken = async (): Promise<string | null> => {
  const issueRefresh = async (): Promise<{ token: string | null; retryable: boolean }> => {
    const refreshResponse = await fetchWithPolicy({
      request: `${API_BASE}/auth/refresh`,
      init: {
        method: 'POST',
        credentials: 'include',
      },
      retries: 0,
    })
    if (refreshResponse.status === 409) {
      return { token: null, retryable: true }
    }
    if (!refreshResponse.ok) {
      return { token: null, retryable: false }
    }
    const refreshPayload = (await refreshResponse.json()) as { token?: string } | null
    const token = typeof refreshPayload?.token === 'string' ? refreshPayload.token.trim() : ''
    return { token: token || null, retryable: false }
  }

  const firstAttempt = await issueRefresh()
  if (firstAttempt.token) return firstAttempt.token
  if (!firstAttempt.retryable) return null

  await sleep(REFRESH_RETRY_DELAY_MS)
  const secondAttempt = await issueRefresh()
  return secondAttempt.token
}

export const useAdminAppState = () => {
  const [authLoading, setAuthLoading] = useState(false)
  const [sessionBootstrapLoading, setSessionBootstrapLoading] = useState(true)
  const [token, setToken] = useState<string | null>(() => getAuthToken())
  const [loginForm, setLoginForm] = useState<AdminLoginForm>({
    username: '',
    password: '',
    otp: '',
    botProtectionToken: '',
    botProtectionProviderId: '',
    twoFactorSessionId: '',
  })
  const [loginStage, setLoginStage] = useState<AdminLoginStage>('credentials')
  const [section, setSection] = useState<string>(() => {
    if (typeof window === 'undefined') return 'dashboard'
    return (
      resolveSectionFromLocation(
        window.location.search,
        window.location.hash,
        window.location.pathname,
      ) ?? 'dashboard'
    )
  })
  const [requiresDeviceTrust, setRequiresDeviceTrust] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedShopProductId, setSelectedShopProductId] = useState<string | null>(null)

  const resolveSectionFromUrl = useCallback(
    (): string | null =>
      resolveSectionFromLocation(window.location.search, window.location.hash, window.location.pathname),
    [],
  )

  const authHeaders = useCallback(
    (withJson = true) => {
      const headers: Record<string, string> = {}
      if (withJson) headers['Content-Type'] = 'application/json'
      if (token) headers.Authorization = `Bearer ${token}`
      return headers
    },
    [token],
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (token) {
      setAuthToken(token)
      return
    }
    clearAuthToken()
  }, [token])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const currentPathname = window.location.pathname
    if (isPublicAdminPath(currentPathname)) {
      setSessionBootstrapLoading(false)
      return
    }
    if (!isProtectedAdminPath(currentPathname)) {
      setSessionBootstrapLoading(false)
      return
    }
    let cancelled = false
    const bootstrapSessionToken = async () => {
      setSessionBootstrapLoading(true)
      try {
        if (!adminSessionBootstrapInFlight) {
          adminSessionBootstrapInFlight = requestSessionBootstrapToken().finally(() => {
            adminSessionBootstrapInFlight = null
          })
        }
        const sessionToken = await adminSessionBootstrapInFlight
        if (!cancelled && sessionToken) {
          setToken(sessionToken)
          await bootstrapAdminCsrfToken(API_BASE)
        }
      } catch {
        // best-effort bootstrap from HttpOnly cookie
      } finally {
        if (!cancelled) {
          setSessionBootstrapLoading(false)
        }
      }
    }
    void bootstrapSessionToken()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !token) return undefined
    if (!isProtectedAdminPath(window.location.pathname)) return undefined

    let cancelled = false
    let refreshTimer: number | null = null

    const scheduleRefresh = (delayMs: number) => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer)
      refreshTimer = window.setTimeout(() => {
        void refreshSession()
      }, delayMs)
    }

    const refreshSession = async () => {
      try {
        if (!adminSessionBootstrapInFlight) {
          adminSessionBootstrapInFlight = requestSessionBootstrapToken().finally(() => {
            adminSessionBootstrapInFlight = null
          })
        }
        const refreshedToken = await adminSessionBootstrapInFlight
        if (cancelled) return
        if (!refreshedToken) {
          scheduleRefresh(SESSION_REFRESH_FAILURE_RETRY_MS)
          return
        }
        setToken(refreshedToken)
        await bootstrapAdminCsrfToken(API_BASE)
      } catch {
        if (!cancelled) scheduleRefresh(SESSION_REFRESH_FAILURE_RETRY_MS)
      }
    }

    const refreshIfDue = () => {
      const delayMs = resolveAccessTokenRefreshDelayMs(token)
      if (delayMs === 0) void refreshSession()
    }

    scheduleRefresh(resolveAccessTokenRefreshDelayMs(token))
    window.addEventListener('focus', refreshIfDue)
    document.addEventListener('visibilitychange', refreshIfDue)

    return () => {
      cancelled = true
      if (refreshTimer !== null) window.clearTimeout(refreshTimer)
      window.removeEventListener('focus', refreshIfDue)
      document.removeEventListener('visibilitychange', refreshIfDue)
    }
  }, [token])

  return {
    authLoading,
    setAuthLoading,
    sessionBootstrapLoading,
    token,
    setToken,
    loginForm,
    setLoginForm,
    loginStage,
    setLoginStage,
    section,
    setSection,
    requiresDeviceTrust,
    setRequiresDeviceTrust,
    selectedUserId,
    setSelectedUserId,
    selectedShopProductId,
    setSelectedShopProductId,
    resolveSectionFromUrl,
    authHeaders,
  }
}
